import { useState, useEffect } from "react";
import { toast } from "sonner";
import { FolderTree, Plus, Trash2, AlertTriangle, X, Save, Pencil } from "lucide-react";
import { logger } from "../../lib/logger";
import { getFaqCategories, createFaqCategory, updateFaqCategory } from "../../lib/api";
import { EMPTY_FAQ_CATEGORIES, TOAST_SAVE_ERROR, TOAST_ADD_ERROR, TOAST_REMOVE_ERROR, TOAST_ADDED, TOAST_UPDATED, TOAST_REMOVED, TOAST_REQUIRED, TOAST_LOAD_ERROR, TIP_EDIT, TIP_DELETE, PLH_CATEGORY } from "../../lib/constants";

export default function AdminFaqCategories({ activeLang = "pt-PT" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  async function load() {
    setLoading(true);
    try {
      const data = await getFaqCategories(activeLang);
      setItems(Array.isArray(data) ? data.filter(c => !c.deleted_at) : []);
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [activeLang]);

  useEffect(() => {
    if (!showAdd && !editing) return;
    function onKey(e) {
      if (e.key === "Escape") { setShowAdd(false); setEditing(null); setEditName(""); }
      if (e.key === "Enter" && !e.shiftKey) {
        if (editing) saveEdit(editing);
        else if (showAdd) addItem();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAdd, editing, editName, newName]);

  async function addItem() {
    if (!newName.trim()) { toast.warning(TOAST_REQUIRED); return; }
    try {
      await createFaqCategory({ name: newName.trim(), active: 1 }, activeLang);
      toast.success(TOAST_ADDED);
      setNewName(""); setShowAdd(false); load();
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_ADD_ERROR);
    }
  }

  function startEdit(item) {
    setEditing(item.uid);
    setEditName(item.name);
  }

  function cancelEdit() {
    setEditing(null);
    setEditName("");
  }

  async function saveEdit(uid) {
    if (!editName.trim()) { toast.warning("Nome é obrigatório"); return; }
    try {
      await updateFaqCategory({ uid, name: editName.trim() });
      toast.success(TOAST_UPDATED);
      setEditing(null); load();
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_SAVE_ERROR);
    }
  }

  function confirmRemove(item) { setConfirm(item); }

  async function handleRemove() {
    if (!confirm) return;
    try {
      await updateFaqCategory({ uid: confirm.uid, deleted_at: new Date().toISOString(), active: 0 });
      toast.success(TOAST_REMOVED);
      setConfirm(null); load();
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_REMOVE_ERROR);
    }
  }

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-champagne border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-id="admin-faq-categories" className="space-y-6">
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4 w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-red-50 shrink-0"><AlertTriangle size={18} className="text-red-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-softblack">Eliminar categoria</p>
                <p className="text-xs text-warmgray mt-1">Tem a certeza que deseja eliminar <strong>"{confirm.name}"</strong>?</p>
              </div>
              <button onClick={() => setConfirm(null)} className="p-1 text-warmgray hover:text-softblack shrink-0"><X size={14} /></button>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirm(null)} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
              <button onClick={handleRemove} className="px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-lg hover:opacity-90">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-softblack">Categorias de FAQ</h3>
          <p className="text-xs text-warmgray mt-1">Organize as FAQs por categoria (Consórcio, Financiamento, etc.)</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary !py-2 !px-4 !text-[10px] flex items-center gap-2">
          <Plus size={14} /> Nova Categoria
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4 w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Plus size={15} /></div>
                <p className="text-sm font-medium text-softblack">Nova Categoria</p>
              </div>
              <button onClick={() => setShowAdd(false)} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Nome <span className="text-red-400">*</span></span>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={PLH_CATEGORY} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" autoFocus />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
              <button onClick={addItem} className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 flex items-center gap-1.5"><Save size={12} /> Adicionar</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4 w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Pencil size={15} /></div>
                <p className="text-sm font-medium text-softblack">Editar Categoria</p>
              </div>
              <button onClick={cancelEdit} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Nome <span className="text-red-400">*</span></span>
              <input value={editName} onChange={e => setEditName(e.target.value)} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" autoFocus />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={cancelEdit} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
              <button onClick={() => saveEdit(editing)} className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 flex items-center gap-1.5"><Save size={12} /> Guardar</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
        {items.length === 0 ? (
          <p className="text-sm text-warmgray py-10 text-center">{EMPTY_FAQ_CATEGORIES}</p>
        ) : (
          <div className="divide-y divide-fendi/10">
            {items.map((item) => (
              <div key={item.uid || item.name} className="flex items-center gap-3 px-5 py-3 hover:bg-fendi/5 transition-colors group">
                <FolderTree size={15} className="text-champagne shrink-0" />
                <span className="flex-1 text-sm text-softblack">{item.name}</span>
                <button onClick={() => startEdit(item)} className="p-1.5 text-warmgray/40 hover:text-champagne transition-colors" title={TIP_EDIT}>
                  <Pencil size={13} />
                </button>
                <button onClick={() => confirmRemove(item)} className="p-1.5 text-warmgray/30 hover:text-red-600 transition-colors" title={TIP_DELETE}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
